/**
 * Pure decision helper for the store-editor Client-contact save path
 * (Phase 1 legacy-client -> first-class-Client migration).
 *
 * The Distributor picks (or creates) a first-class Client in the Store
 * editor's Client Contact card. Linkage to the store is persisted ONLY
 * through the tenant-guarded association endpoints:
 *
 *   - attach: POST   /clients/<id>/stores       (cap-gated client.update)
 *   - detach: DELETE /clients/<id>/stores/<sid>
 *
 * stores.client_id is NEVER written by the SAFRS campaign create or the
 * update_campaign RPC -- the attach endpoint is the sole writer.
 *
 * Given the previously-persisted client id and the newly-selected client
 * id, this function returns a plan describing which guarded calls to make.
 * It is pure (no I/O, no side effects) so the attach/detach decision can
 * be unit-tested without mounting the (very large) StoreAddEdit component.
 *
 * @param {number|string|null|undefined} oldClientId - the client id
 *   currently persisted on the store (from the last server load), or null.
 * @param {number|string|null|undefined} newClientId - the client id
 *   selected in the editor this save, or null when no selection was made.
 * @returns {{ attach: (number|string|null), detach: (number|string|null) }}
 *   - attach: client id to attach, or null when nothing to attach.
 *   - detach: client id to detach, or null when nothing to detach.
 */
export function resolveClientContactChange(oldClientId, newClientId) {
    const oldId = oldClientId ?? null
    const newId = newClientId ?? null

    // Value-based comparison: the server returns client_id as a number
    // while the picker may surface a string. "3" and 3 are the same link.
    const same = String(oldId ?? '') === String(newId ?? '')

    // Phase 1 has no clear-client / unlink UI. A null newId means "no
    // selection made", NOT "remove the existing client". Stay conservative:
    // never detach when there is no replacement to attach.
    if (newId == null) {
        return { attach: null, detach: null }
    }

    // No change -- nothing to do.
    if (same) {
        return { attach: null, detach: null }
    }

    // newId is set and differs from oldId: attach the new client, and
    // detach the previously-attached one if there was a (different) one.
    return {
        attach: newId,
        detach: oldId != null ? oldId : null,
    }
}
